/**
 * Login Page
 * Handles user authentication with ITS ID and password
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import bismillahSrc from '@/images/bismillah.png';
import slide1Src from '@/images/Picture1_scaled.png';
import slide2Src from '@/images/Picture2_scaled.png';

const slides = [slide1Src, slide2Src];

function resolveRedirectTarget(state: unknown): string | null {
  const from = (state as { from?: string | { pathname?: string; search?: string; hash?: string } })?.from;
  if (!from) return null;
  if (typeof from === 'string') return from;

  const pathname = from.pathname || '';
  const search = from.search || '';
  const hash = from.hash || '';
  const target = `${pathname}${search}${hash}`;
  return target || null;
}

export function Login() {
  const [itsId, setItsId] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ itsId?: string; password?: string }>({});
  const [slideIndex, setSlideIndex] = useState(0);

  const { login, isLoading } = useAuthStore();
  const { showToast } = useUIStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the return URL from location state, or default based on role
  const from = resolveRedirectTarget(location.state);

  // Auto-advance slideshow — restart timer on manual navigation
  useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((i) => (i + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [slideIndex]);

  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const newErrors: { itsId?: string; password?: string } = {};

    if (!itsId) {
      newErrors.itsId = 'ITS ID is required';
    } else if (!/^\d+$/.test(itsId)) {
      newErrors.itsId = 'ITS ID must contain only digits';
    }

    // No minimum length check on login — migrated users may have shorter passwords.
    // Length enforcement is at registration/password-change only.
    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Reset errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      // Call login action
      await login(itsId, password);

      // Show success message
      showToast('Login successful!', 'success');

      // Get user to determine redirect
      const user = useAuthStore.getState().user;

      // Redirect based on role or return URL
      if (from) {
        navigate(from, { replace: true });
      } else {
        switch (user?.role) {
          case 'ADMIN':
            navigate('/admin/dashboard', { replace: true });
            break;
          case 'CATERER':
            navigate('/caterer/dashboard', { replace: true });
            break;
          case 'MEMBER':
          default:
            navigate('/rsvp', { replace: true });
            break;
        }
      }
    } catch (error) {
      // Show error toast
      const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';
      showToast(errorMessage, 'error');
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen warm-gradient-bg p-4">
      <div className="w-full max-w-xl flex flex-col items-center">
        {/* Branding */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={bismillahSrc}
            alt="Bismillah"
            className="w-64 sm:w-72 h-auto mb-5 drop-shadow-sm dark-logo-filter"
          />
          <h1 className="text-xl sm:text-2xl font-semibold text-stone-800 dark:text-stone-200 tracking-tight text-center">
            Faiz-ul-Mawaid al-Burhaniyah
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400 mt-1 tracking-widest uppercase">
            San Jose
          </p>
        </div>

        {/* Image slideshow */}
        <div className="relative grid w-full mb-8 rounded-xl overflow-hidden shadow-md">
          {slides.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="col-start-1 row-start-1 w-full h-auto transition-opacity duration-1000 ease-in-out"
              style={{ opacity: slideIndex === i ? 1 : 0 }}
            />
          ))}
          {/* Slide indicators */}
          <div className="col-start-1 row-start-1 self-end justify-self-center pb-2 flex gap-1.5 z-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlideIndex(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  slideIndex === i
                    ? 'bg-white w-4'
                    : 'bg-white/50'
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Login Card */}
        <Card className="w-full max-w-md shadow-lg border-stone-200/60 dark:border-stone-700/60">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* ITS ID Input */}
              <div className="space-y-2">
                <Label htmlFor="itsId">ITS ID</Label>
                <Input
                  id="itsId"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Enter your ITS ID"
                  value={itsId}
                  onChange={(e) => setItsId(e.target.value.replace(/\D/g, ''))}
                  disabled={isLoading}
                  aria-invalid={!!errors.itsId}
                  required
                />
                {errors.itsId && (
                  <p className="text-sm text-destructive">{errors.itsId}</p>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  aria-invalid={!!errors.password}
                  required
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Additional Info */}
            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>
                Don't have an account?{' '}
                <Link to="/register" className="text-primary hover:underline">
                  Register here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
